import { useEffect, useState, useMemo } from "react";
import api, { fmtPKR, fmtDateTime } from "../lib/api";
import {
  TrendingUp,
  Receipt,
  Package,
  AlertTriangle,
  ShoppingCart,
  ArrowUpRight,
  ArrowRight,
  Circle,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
} from "recharts";

/* ---------- Little helpers ---------- */

// Sparkline mini-chart
function Sparkline({ data, color = "#1e40af" }) {
  return (
    <div className="h-10 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Line
            type="monotone"
            dataKey="total"
            stroke={color}
            strokeWidth={1.6}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Editorial section marker: roman + rule
function SectionMark({ roman, label }) {
  return (
    <div className="flex items-center gap-4 mb-5">
      <div className="font-serif italic text-blue-700 text-[15px] leading-none">{roman}</div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-slate-500">{label}</div>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

// Corner-mark decoration (like precision instrument)
function CornerMark({ label }) {
  return (
    <div className="absolute top-3 right-4 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] text-slate-400">
      <span className="w-1 h-1 rounded-full bg-slate-300" />
      {label}
    </div>
  );
}

/* ---------- Main ---------- */

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState({ daily: [], monthly: [] });
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/charts"),
        ]);
        setStats(s.data);
        setCharts(c.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const spark = useMemo(() => charts.daily.slice(-14), [charts.daily]);
  const monthlySpark = useMemo(() => charts.monthly.slice(-12), [charts.monthly]);
  const hour = now.getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : hour < 21 ? "Good evening" : "Working late";
  const dateStr = now.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div data-testid="dashboard-page">
      {/* ---------- TOP TICKER STRIP ---------- */}
      <div
        className="border-b border-slate-200 px-4 sm:px-10 py-2 flex items-center justify-between gap-3 text-[11px] font-mono-tabular text-slate-500 overflow-x-auto"
        style={{ background: "linear-gradient(180deg, #fff 0%, #fafaf9 100%)" }}
      >
        <div className="flex items-center gap-4 sm:gap-6 shrink-0">
          <div className="flex items-center gap-2">
            <Circle size={7} className="fill-emerald-500 text-emerald-500" />
            <span className="uppercase tracking-[0.24em] text-[10px]">System nominal</span>
          </div>
          <span className="text-slate-300 hidden sm:inline">|</span>
          <span className="uppercase tracking-[0.24em] text-[10px] hidden sm:inline">
            Console <span className="text-slate-800">v1.0</span>
          </span>
          <span className="text-slate-300 hidden md:inline">|</span>
          <span className="uppercase tracking-[0.24em] text-[10px] hidden md:inline">
            Tarnol, Islamabad <span className="text-slate-800">· PK</span>
          </span>
        </div>
        <div className="flex items-center gap-4 sm:gap-6 shrink-0">
          <span className="uppercase tracking-[0.24em] text-[10px]">
            Currency <span className="text-slate-800">PKR ₨</span>
          </span>
          <span className="text-slate-300 hidden sm:inline">|</span>
          <span className="uppercase tracking-[0.24em] text-[10px] hidden sm:inline">
            Invoice <span className="text-slate-800">INV-{String((stats?.month_count ?? 0) + 1).padStart(4, "0")}</span>
          </span>
        </div>
      </div>

      {/* ---------- MASTHEAD ---------- */}
      <div className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 sm:pb-8 relative">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="h-px w-6 sm:w-10 bg-slate-900" />
              <div className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.32em] text-slate-500">
                {dateStr}
              </div>
            </div>
            <h1 className="font-serif text-[36px] sm:text-[52px] lg:text-[64px] leading-[0.95] text-slate-900 tracking-tight">
              {greeting}, <span className="italic text-blue-700">operator</span>.
            </h1>
            <p className="text-sm text-slate-500 mt-3 sm:mt-4 max-w-lg leading-relaxed">
              This is your daily read-out. Sales, invoices, stock levels and the pulse of the
              workshop — laid out as one continuous ledger.
            </p>
          </div>

          <Link
            to="/pos"
            data-testid="quick-pos-btn"
            className="group inline-flex items-center gap-3 pl-5 pr-3 py-3 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium transition-all"
            style={{ boxShadow: "0 8px 24px -8px rgba(15,23,42,0.4)" }}
          >
            <ShoppingCart size={16} strokeWidth={1.8} />
            <span>Ring up a sale</span>
            <span className="ml-1 w-7 h-7 rounded flex items-center justify-center bg-blue-600 group-hover:bg-blue-500 transition-colors">
              <ArrowRight size={14} strokeWidth={2} />
            </span>
          </Link>
        </div>
      </div>

      {/* ---------- HERO BLOCK — asymmetric ---------- */}
      <div className="px-4 sm:px-10">
        <SectionMark roman="I." label="Today's revenue" />
        <div className="grid grid-cols-12 gap-4 mb-10">
          {/* HERO — Big number */}
          <div
            className="col-span-12 lg:col-span-7 relative overflow-hidden rounded-xl p-8 grain"
            style={{
              background:
                "linear-gradient(135deg, #05070d 0%, #0b1220 60%, #0a1a3d 100%)",
              boxShadow: "0 20px 40px -20px rgba(5,7,13,0.5)",
            }}
            data-testid="kpi-today-sales"
          >
            {/* Decorative rings */}
            <svg
              className="absolute -right-24 -top-16 opacity-[0.06]"
              width="360"
              height="360"
              viewBox="0 0 360 360"
              fill="none"
            >
              <circle cx="180" cy="180" r="60" stroke="#fff" strokeWidth="1" />
              <circle cx="180" cy="180" r="100" stroke="#fff" strokeWidth="1" />
              <circle cx="180" cy="180" r="140" stroke="#fff" strokeWidth="1" />
              <circle cx="180" cy="180" r="180" stroke="#fff" strokeWidth="1" />
            </svg>

            <div className="flex items-start justify-between relative z-10">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400">
                    Today · Real-time
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                  Gross Sales
                </div>
                <div className="big-number text-white text-[92px] leading-[0.9] mt-4 tracking-tight">
                  {stats?.today_sales?.toLocaleString("en-US") || "0"}
                </div>
                <div className="text-blue-300 text-sm mt-2 font-serif italic">
                  Rs · Pakistani Rupees
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                  Invoices
                </div>
                <div className="font-serif text-4xl text-white mt-1">
                  {stats?.today_count ?? 0}
                </div>
              </div>
            </div>

            <div className="relative z-10 mt-8 pt-6 border-t border-white/10 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">
                  Last 14 days · Momentum
                </div>
                <div className="text-slate-400 text-[11px]">
                  A quiet line telling a loud story.
                </div>
              </div>
              <div className="w-56">
                <Sparkline data={spark} color="#60a5fa" />
              </div>
            </div>
          </div>

          {/* Right column — stacked mini metrics */}
          <div className="col-span-12 lg:col-span-5 grid grid-cols-1 gap-4">
            <MiniMetric
              label="This Month"
              value={fmtPKR(stats?.month_sales)}
              sub={`${stats?.month_count || 0} invoices this month`}
              icon={Receipt}
              accent="#1e40af"
              spark={monthlySpark}
              testid="kpi-month-sales"
            />
            <div className="grid grid-cols-2 gap-4">
              <MiniMetricSm
                label="Products"
                value={stats?.total_products ?? 0}
                sub="in inventory"
                icon={Package}
                accent="#0f172a"
                testid="kpi-products"
              />
              <MiniMetricSm
                label="Low Stock"
                value={stats?.low_stock_count ?? 0}
                sub="need reorder"
                icon={AlertTriangle}
                accent="#b91c1c"
                warning={(stats?.low_stock_count ?? 0) > 0}
                testid="kpi-low-stock"
              />
            </div>
          </div>
        </div>

        <SectionMark roman="II." label="Trend" />
        <div className="grid grid-cols-12 gap-4 mb-10">
          <div
            className="col-span-12 lg:col-span-7 card-premium p-7 relative"
            data-testid="chart-daily"
          >
            <CornerMark label="14-DAY · DAILY" />
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">
                Chapter · Momentum
              </div>
              <div className="font-serif text-3xl text-slate-900 leading-tight">
                A fortnight in figures
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.daily} margin={{ left: -8, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fillHero" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1e40af" stopOpacity={0.32} />
                      <stop offset="100%" stopColor="#1e40af" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#e5e7eb" strokeDasharray="2 4" />
                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    fontSize={10}
                    tickFormatter={(d) => d.slice(5)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={10}
                    tickFormatter={(v) => v.toLocaleString()}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                  />
                  <Tooltip
                    formatter={(v) => fmtPKR(v)}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    }}
                    labelStyle={{ color: "#0f172a", fontWeight: 600 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#1e40af"
                    strokeWidth={2}
                    fill="url(#fillHero)"
                    activeDot={{ r: 5, fill: "#1e40af", stroke: "#fff", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            className="col-span-12 lg:col-span-5 card-premium p-7 relative"
            data-testid="chart-monthly"
          >
            <CornerMark label="12-MONTH · MONTHLY" />
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">
                Chapter · Year
              </div>
              <div className="font-serif text-3xl text-slate-900 leading-tight">
                Twelve seasons
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.monthly} margin={{ left: -8, right: 4, top: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#e5e7eb" strokeDasharray="2 4" />
                  <XAxis
                    dataKey="month"
                    stroke="#94a3b8"
                    fontSize={10}
                    tickFormatter={(m) => m.slice(5)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={10}
                    tickFormatter={(v) => v.toLocaleString()}
                    axisLine={false}
                    tickLine={false}
                    width={54}
                  />
                  <Tooltip
                    formatter={(v) => fmtPKR(v)}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    }}
                  />
                  <Bar dataKey="total" fill="#0f172a" radius={[4, 4, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ---------- LEDGER-STYLE TABLES ---------- */}
        <SectionMark roman="III." label="Ledger" />
        <div className="grid grid-cols-12 gap-4 pb-10">
          <div
            className="col-span-12 lg:col-span-7 card-premium overflow-hidden"
            data-testid="recent-sales-card"
          >
            <div className="px-7 pt-6 pb-5 flex items-end justify-between border-b border-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">
                  Journal
                </div>
                <div className="font-serif text-2xl text-slate-900">Recent invoices</div>
              </div>
              <Link
                to="/reports"
                className="text-[11px] uppercase tracking-[0.24em] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              >
                All entries <ArrowUpRight size={12} />
              </Link>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[9.5px] uppercase tracking-[0.24em] text-slate-500 bg-slate-50/60">
                  <th className="px-7 py-3 text-left font-medium">Invoice</th>
                  <th className="px-4 py-3 text-left font-medium">Customer</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-7 py-3 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_sales || []).map((s, i) => (
                  <tr
                    key={s.id}
                    className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors group"
                  >
                    <td className="px-7 py-3">
                      <div className="flex items-center gap-3">
                        <span className="font-mono-tabular text-[10px] text-slate-400">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-mono-tabular text-xs text-slate-900">
                          {s.invoice_no}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{s.customer_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs font-mono-tabular">
                      {fmtDateTime(s.date)}
                    </td>
                    <td className="px-7 py-3 text-right tabular text-slate-900 font-semibold">
                      {fmtPKR(s.grand_total)}
                    </td>
                  </tr>
                ))}
                {!loading && (stats?.recent_sales || []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-7 py-16 text-center">
                      <div className="font-serif italic text-slate-400">No entries yet</div>
                      <div className="text-xs text-slate-400 mt-1">
                        Head to POS to record your first sale.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div
            className="col-span-12 lg:col-span-5 card-premium overflow-hidden"
            data-testid="low-stock-card"
          >
            <div className="px-7 pt-6 pb-5 flex items-end justify-between border-b border-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">
                  Reorder list
                </div>
                <div className="font-serif text-2xl text-slate-900">
                  <span className="text-red-700">Low</span> on stock
                </div>
              </div>
              <Link
                to="/inventory"
                className="text-[11px] uppercase tracking-[0.24em] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              >
                Manage <ArrowUpRight size={12} />
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {(stats?.low_stock_items || []).map((p) => (
                <div
                  key={p.id}
                  className="px-7 py-3 flex items-center justify-between hover:bg-red-50/40"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 truncate">{p.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono-tabular">
                      {p.product_code}
                      {p.brand ? ` · ${p.brand}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="font-serif text-2xl text-red-700 leading-none tabular">
                      {p.current_stock}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">
                      min {p.minimum_stock}
                    </div>
                  </div>
                </div>
              ))}
              {!loading && (stats?.low_stock_items || []).length === 0 && (
                <div className="px-7 py-16 text-center">
                  <div className="font-serif italic text-slate-400">All shelves stocked</div>
                  <div className="text-xs text-slate-400 mt-1">Nothing to reorder.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom rule */}
      <div className="px-4 sm:px-10 pb-10 pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-slate-400 flex-wrap gap-2">
          <span>Afridi&amp;Co. · Established Tarnol, Islamabad</span>
          <span className="font-mono-tabular">End of ledger · {now.getFullYear()}</span>
        </div>
        <div className="mt-2 flex items-center justify-center text-[10px] uppercase tracking-[0.24em] text-slate-400 text-center">
          <span>
            Software developed by{" "}
            <span className="text-slate-600 font-medium">Code Wave Solutions</span>
            <span className="mx-2 text-slate-300">·</span>
            <span className="font-mono-tabular text-slate-600">0348 6811636</span>
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Metric sub-components ---------- */

function MiniMetric({ label, value, sub, icon: Icon, accent, spark, testid }) {
  return (
    <div
      className="card-premium p-6 relative overflow-hidden"
      data-testid={testid}
    >
      <div
        className="absolute top-0 left-0 h-full w-[3px]"
        style={{ background: accent }}
      />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">
            {label}
          </div>
          <div className="big-number text-slate-900 text-[36px] leading-none mt-2">{value}</div>
          <div className="text-xs text-slate-500 mt-2">{sub}</div>
        </div>
        <Icon size={17} strokeWidth={1.5} className="text-slate-400" />
      </div>
      {spark && spark.length > 0 && (
        <div className="mt-3">
          <Sparkline data={spark} color={accent} />
        </div>
      )}
    </div>
  );
}

function MiniMetricSm({ label, value, sub, icon: Icon, accent, warning, testid }) {
  return (
    <div
      className={`card-premium p-5 relative overflow-hidden ${warning ? "" : ""}`}
      style={warning ? { background: "linear-gradient(180deg, #fef2f2 0%, #fff 100%)" } : {}}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-2">
            {label}
          </div>
          <div
            className="big-number text-[36px] leading-none"
            style={{ color: warning ? "#b91c1c" : "#0f172a" }}
          >
            {value}
          </div>
          <div className="text-[11px] text-slate-500 mt-2">{sub}</div>
        </div>
        <Icon size={16} strokeWidth={1.5} style={{ color: warning ? accent : "#94a3b8" }} />
      </div>
    </div>
  );
}
